/**
 * Unified Protected Route Component
 * Uses the new unified auth system
 * 
 * @author Employee Management System
 * @version 3.0.0
 */

import React, { useEffect } from 'react';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { checkTokenValidity, selectIsAuthReady } from '../../store/slices/authSlice';
import { PUBLIC_ROUTES } from '../../api/constants';
import AuthService from '../../services/authService';
import useAuth from '../../hooks/useAuth';

export const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  requireVerification = true, 
  requiredPermissions = [],
  requiredModule = null,
  requireAny = false
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  
  // CRITICAL FIX: All hooks MUST be called at the top (React rules)
  const { token, isVerified, email, accessDenied } = useSelector(state => state.auth);
  const isAuthReady = useSelector(selectIsAuthReady);
  const isAuthenticated = !!token;
  const auth = useAuth(); // Safe: useAuth handles null user internally
  
  // Check token validity on route change
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(checkTokenValidity());
    }
  }, [dispatch, isAuthenticated, location.pathname]);
  
  // 🛡️ STEP 1: Wait for auth system to be ready
  // This prevents race conditions and null reference errors
  if (!isAuthReady) {
    return <LoadingSpinner fullPage size="md" text="Oturum kontrol ediliyor..." />;
  }
  
  // 🛡️ STEP 2: Public routes can bypass all checks
  if (PUBLIC_ROUTES.includes(location.pathname)) {
    return children;
  }
  
  // 🛡️ STEP 3: Authentication check - MUST BE DONE EARLY
  // If not authenticated, redirect immediately without any further checks
  if (!isAuthenticated) {
    // If user has email but not verified, send to verify page
    if (email && !isVerified) {
      return <Navigate to="/verify" state={{ from: location }} replace />;
    }
    
    // Otherwise redirect to login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // 🛡️ NOW IT'S SAFE: User is authenticated and auth is ready
  // We can safely use auth object (already initialized at top)
  
  // Additional session validation
  const isSessionValid = isAuthenticated && AuthService.validateSession();
  
  // Check if token is valid, if not redirect to login
  if (!isSessionValid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verification check (if required)
  if (requireVerification && !isVerified) {
    return <Navigate to="/verify" replace />;
  }

  // Access denied check from auth state
  if (accessDenied) {
    return <Navigate to="/access-denied" replace />;
  }

  // **UNIFIED ACCESS CONTROL: Use single source of truth (canAccessRoute)**
  const routeConfig = {
    allowedRoles,
    requiredPermissions,
    requiredModule,
    requireAny
  };

  const hasRouteAccess = auth.canAccessRoute(routeConfig);

  if (!hasRouteAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return children;
};